import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { termsOfServicePtBR } from '@ibk/shared-assets';
import { useTranslation } from '@ibk/shared-translation';
import { cleanDocumentHTML, SCROLL_END_THRESHOLD_PX, TERMS_ACCEPTANCE_LOG_PREFIX, TERMS_ACCEPTANCE_MODAL_DEFAULT_TEST_ID, TERMS_IFRAME_TYPOGRAPHY_STYLE_ELEMENT_ID, } from '@ibk/shared-utils';
import { resolveTermsModalScrollLayout } from '../../components/TermsAcceptanceModal/termsAcceptanceModalLayout';
import { useLinkedTermsFromUrl } from '../useLinkedTermsFromUrl';
import { useTermsIframeScrollEndTracking } from '../useTermsIframeScrollEndTracking';
import { useTermsModalAgreedResetOnOpen } from '../useTermsModalAgreedResetOnOpen';
import { useTermsModalBlockEscapeKey } from '../useTermsModalBlockEscapeKey';
import { useTermsScrollBoxEndSync } from '../useTermsScrollBoxEndSync';
export { SCROLL_END_THRESHOLD_PX };
export function useTermsAcceptanceModal(props) {
    const { isOpen, onAccept, children, termsHtml, modalTitle, modalSubtitle, termsDocumentUrl, 'data-testid': testId = TERMS_ACCEPTANCE_MODAL_DEFAULT_TEST_ID, } = props;
    const { t } = useTranslation();
    const [agreed, setAgreed] = useState(false);
    const [hasScrolledTermsToEnd, setHasScrolledTermsToEnd] = useState(false);
    const [isAcceptInFlight, setIsAcceptInFlight] = useState(false);
    const acceptInFlightRef = useRef(false);
    const linkedTerms = useLinkedTermsFromUrl(isOpen, termsDocumentUrl, children);
    const termsIframeRef = useRef(null);
    const scrollBoxRef = useRef(null);
    const iframeScrollCleanupRef = useRef(null);
    const onTermsIframeLoad = useCallback(() => {
        try {
            const doc = termsIframeRef.current?.contentDocument;
            if (!doc?.head) {
                return;
            }
            const id = TERMS_IFRAME_TYPOGRAPHY_STYLE_ELEMENT_ID;
            if (doc.getElementById(id)) {
                return;
            }
            const style = doc.createElement('style');
            style.id = id;
            style.textContent =
                'p{font-size:13px}h2{font-size:15px;text-align:left;font-weight:600;margin:0 0 .5em;line-height:1.3}';
            doc.head.appendChild(style);
        }
        catch (cause) {
            if (typeof process !== 'undefined' && process.env.NODE_ENV === 'development') {
                console.debug(`${TERMS_ACCEPTANCE_LOG_PREFIX} Tipografia do iframe não aplicada (ex.: cross-origin).`, cause);
            }
        }
    }, []);
    const { showIframeFallback, showLinkedLoading } = resolveTermsModalScrollLayout(termsDocumentUrl, children, linkedTerms);
    const updateScrollEndFromScrollBox = useCallback(() => {
        const el = scrollBoxRef.current;
        if (!el || showIframeFallback || showLinkedLoading) {
            if (showLinkedLoading) {
                setHasScrolledTermsToEnd(false);
            }
            return;
        }
        const { scrollTop, scrollHeight, clientHeight } = el;
        const needsScroll = scrollHeight > clientHeight + SCROLL_END_THRESHOLD_PX;
        const atEnd = !needsScroll || scrollTop + clientHeight >= scrollHeight - SCROLL_END_THRESHOLD_PX;
        setHasScrolledTermsToEnd(atEnd);
    }, [showIframeFallback, showLinkedLoading]);
    useTermsModalAgreedResetOnOpen(isOpen, setAgreed);
    useTermsScrollBoxEndSync({
        scrollBoxRef,
        isOpen,
        termsHtml,
        termsDocumentUrl,
        linkedTerms,
        children,
        showIframeFallback,
        showLinkedLoading,
        updateScrollEndFromScrollBox,
    });
    useTermsIframeScrollEndTracking(isOpen, showIframeFallback, termsDocumentUrl, termsIframeRef, iframeScrollCleanupRef, setHasScrolledTermsToEnd);
    useTermsModalBlockEscapeKey(isOpen);
    const wasOpenRef = useRef(false);
    useEffect(() => {
        if (!isOpen) {
            wasOpenRef.current = false;
            return;
        }
        if (wasOpenRef.current) {
            return;
        }
        wasOpenRef.current = true;
        const id = window.requestAnimationFrame(() => {
            scrollBoxRef.current?.focus();
        });
        return () => window.cancelAnimationFrame(id);
    }, [isOpen]);
    const titleText = modalTitle ?? t('terms-acceptance.title');
    const subtitleText = modalSubtitle ?? t('terms-acceptance.subtitle');
    const sanitizedTermsHtml = useMemo(() => {
        const source = termsHtml ?? termsOfServicePtBR;
        return cleanDocumentHTML(source);
    }, [termsHtml]);
    const handleConfirm = useCallback(async () => {
        if (!agreed || acceptInFlightRef.current) {
            return;
        }
        acceptInFlightRef.current = true;
        setIsAcceptInFlight(true);
        try {
            await Promise.resolve(onAccept());
        }
        catch {
        }
        finally {
            acceptInFlightRef.current = false;
            setIsAcceptInFlight(false);
        }
    }, [agreed, onAccept]);
    const noopClose = useCallback(() => {
    }, []);
    return {
        testId,
        titleText,
        subtitleText,
        scrollBoxRef,
        termsIframeRef,
        updateScrollEndFromScrollBox,
        agreed,
        setAgreed,
        hasScrolledTermsToEnd,
        isAcceptInFlight,
        handleConfirm,
        noopClose,
        checkboxLabel: t('terms-acceptance.checkbox-label'),
        checkboxAria: t('terms-acceptance.checkbox-aria'),
        scrollRegionAria: t('terms-acceptance.scroll-region-label'),
        documentLoadingAnnouncement: t('terms-acceptance.document-loading-announcement'),
        primaryButtonLabel: t('terms-acceptance.primary-button'),
        linkedTerms,
        sanitizedTermsHtml,
        onTermsIframeLoad,
    };
}
